import { Cpu } from "lucide-react";
import { useCallback } from "react";
import { CircularProgress } from "@/components/monitoring/CircularProgress";
import { StatCard } from "@/components/shared/StatCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGatewayRpc } from "@/hooks/useGatewayRpc";
import { usePolling } from "@/hooks/usePolling";
import { formatBytes, formatDuration } from "@/lib/chart-helpers";

type BackendSample = {
  ts: number;
  cpu: { user: number; system: number; percent: number };
  memory: { rss: number; heapUsed: number; heapTotal: number; external: number; percentUsed: number };
  disk: { total: number; free: number; used: number; percentUsed: number };
  uptime: number;
  pid: number;
  eventLoopLag: number | null;
};

export function ProcessMonitor() {
  const { rpc, state } = useGatewayRpc();

  const fetcher = useCallback(async () => {
    const res = await rpc<{ samples: BackendSample[] }>("metrics.system", { since: Date.now() - 300_000 });
    return res.samples;
  }, [rpc]);

  const { data: samples } = usePolling(fetcher, 10_000, { enabled: state === "connected" });
  const latest = samples?.at(-1);

  if (state !== "connected" && !samples) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <h1 className="text-2xl font-semibold">Process Monitor</h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {state === "connecting" ? "Connecting to gateway..." : "Gateway disconnected"}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Process Monitor</h1>

      {/* System stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard title="CPU" value={latest ? `${latest.cpu.percent.toFixed(1)}%` : "--"} />
        <StatCard title="Memory" value={latest ? `${latest.memory.percentUsed.toFixed(1)}%` : "--"} />
        <StatCard title="Uptime" value={latest ? formatDuration(latest.uptime * 1000) : "--"} />
        <StatCard title="Disk" value={latest ? `${latest.disk.percentUsed.toFixed(1)}%` : "--"} />
      </div>

      {/* Resource gauges */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardContent className="flex flex-col items-center pt-6">
            <CircularProgress value={latest?.cpu.percent ?? 0} label="CPU" size={100} />
            <div className="mt-3 grid grid-cols-2 gap-4 text-xs">
              <div className="text-center">
                <p className="text-muted-foreground">User</p>
                <p className="font-medium">{latest ? `${latest.cpu.user.toFixed(0)}ms` : "--"}</p>
              </div>
              <div className="text-center">
                <p className="text-muted-foreground">System</p>
                <p className="font-medium">{latest ? `${latest.cpu.system.toFixed(0)}ms` : "--"}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col items-center pt-6">
            <CircularProgress value={latest?.memory.percentUsed ?? 0} label="Memory" size={100} />
            <div className="mt-3 grid grid-cols-2 gap-4 text-xs">
              <div className="text-center">
                <p className="text-muted-foreground">RSS</p>
                <p className="font-medium">{latest ? formatBytes(latest.memory.rss) : "--"}</p>
              </div>
              <div className="text-center">
                <p className="text-muted-foreground">Heap Used</p>
                <p className="font-medium">{latest ? formatBytes(latest.memory.heapUsed) : "--"}</p>
              </div>
              <div className="text-center">
                <p className="text-muted-foreground">Heap Total</p>
                <p className="font-medium">{latest ? formatBytes(latest.memory.heapTotal) : "--"}</p>
              </div>
              <div className="text-center">
                <p className="text-muted-foreground">External</p>
                <p className="font-medium">{latest ? formatBytes(latest.memory.external) : "--"}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex flex-col items-center pt-6">
            <CircularProgress value={latest?.disk.percentUsed ?? 0} label="Disk" size={100} />
            <div className="mt-3 grid grid-cols-2 gap-4 text-xs">
              <div className="text-center">
                <p className="text-muted-foreground">Total</p>
                <p className="font-medium">{latest ? formatBytes(latest.disk.total) : "--"}</p>
              </div>
              <div className="text-center">
                <p className="text-muted-foreground">Free</p>
                <p className="font-medium">{latest ? formatBytes(latest.disk.free) : "--"}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Runtime info */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Cpu className="h-4 w-4" /> Runtime Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm lg:grid-cols-4">
            <div>
              <p className="text-xs text-muted-foreground">PID</p>
              <p className="font-mono">{latest?.pid ?? "--"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Event Loop Lag</p>
              <p className="font-mono">{latest?.eventLoopLag != null ? `${latest.eventLoopLag.toFixed(2)}ms` : "--"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Samples</p>
              <p className="font-mono">{samples?.length ?? 0}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Latest</p>
              <p className="font-mono text-xs">{latest ? new Date(latest.ts).toLocaleTimeString() : "--"}</p>
            </div>
          </div>
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-muted-foreground dark:border-slate-800 dark:bg-slate-900">
            <p>
              <strong>Process tree</strong> (per-PID + parent/child topology)
              requires <code>/api/admin/processes</code> on the runtime side
              — still unbuilt; the gauges above are CPU/memory/disk roll-ups
              only.
            </p>
            <p className="mt-2">
              <strong>Chrome / VNC status</strong> is no longer surfaced here
              — the legacy <code>/api/admin/browser/status</code> endpoint was
              superseded 2026-06-03 by the per-user{" "}
              <code>/api/lx/openclaw/browser/status</code> surface (cross-user
              proxied), driving the live Chromium viewer in{" "}
              <code>/runtime-parameters/&lt;userId&gt;</code> Browser tab
              (P4.D.X, commit 720bb5f2). Operators reach for that route to
              watch / force-stop / pause / resume / wipe-cookies a user's
              browser session.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
